/**
 * The upload half of the image picker (MK-4a).
 *
 * Moved verbatim out of `editing/image-picker.js` when the picker became a
 * collection browser: the file gate, the raster downscale, and the drop target
 * are the same code, extracted so the façade holds a session and not a
 * pipeline. `prepareUploadFile` keeps its exported name because two other
 * modules import it directly (`ui/pwa-install-settings.js`,
 * `editing/image-editor.js`).
 */

export const ACCEPTED_IMAGE_TYPES =
  'image/jpeg,image/png,image/webp,image/gif,image/avif,image/svg+xml';
const ACCEPTED_UPLOAD_MIME_TYPES = new Set(ACCEPTED_IMAGE_TYPES.split(','));
const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
const DOWNSCALE_TRIGGER_BYTES = 4 * 1024 * 1024;
const MAX_RASTER_DIMENSION = 2560;

function isHeicFile(file) {
  const type = String(file?.type || '').toLowerCase();
  const name = String(file?.name || '').toLowerCase();
  return type === 'image/heic' || type === 'image/heif' || /\.(heic|heif)$/i.test(name);
}

function isAcceptedUploadFile(file) {
  if (!file) return false;
  if (isHeicFile(file)) return false;
  const type = String(file.type || '').toLowerCase();
  if (ACCEPTED_UPLOAD_MIME_TYPES.has(type)) return true;
  return /\.(jpe?g|png|webp|gif|avif|svg)$/i.test(String(file.name || ''));
}

function canDownscaleFile(file) {
  const type = String(file?.type || '').toLowerCase();
  if (!/^image\/(jpeg|png|webp)$/.test(type)) return false;
  return (
    typeof document !== 'undefined' && typeof URL !== 'undefined' && typeof Image === 'function'
  );
}

function makeDownscaledFilename(name, type) {
  const ext = type === 'image/png' ? 'png' : type === 'image/webp' ? 'webp' : 'jpg';
  const base =
    String(name || 'upload')
      .replace(/\.[^.]+$/, '')
      .replace(/[^a-z0-9._-]+/gi, '-')
      .replace(/^-+|-+$/g, '') || 'upload';
  return `${base}.${ext}`;
}

function downscaleImageFile(file) {
  if (!canDownscaleFile(file)) return Promise.resolve(file);
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    const done = (nextFile) => {
      try {
        URL.revokeObjectURL(url);
      } catch {
        /* revoking a url the browser already dropped is not an error */
      }
      resolve(nextFile || file);
    };
    img.onload = () => {
      const width = img.naturalWidth || img.width || 0;
      const height = img.naturalHeight || img.height || 0;
      const maxDim = Math.max(width, height);
      if (
        !width ||
        !height ||
        (maxDim <= MAX_RASTER_DIMENSION && file.size <= DOWNSCALE_TRIGGER_BYTES)
      ) {
        done(file);
        return;
      }
      const scale = Math.min(1, MAX_RASTER_DIMENSION / maxDim);
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(width * scale));
      canvas.height = Math.max(1, Math.round(height * scale));
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        done(file);
        return;
      }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const outType = file.type === 'image/png' ? 'image/png' : file.type || 'image/jpeg';
      canvas.toBlob(
        (blob) => {
          if (!blob || blob.size >= file.size) {
            done(file);
            return;
          }
          done(
            new File([blob], makeDownscaledFilename(file.name, outType), {
              type: outType,
              lastModified: Date.now()
            })
          );
        },
        outType,
        0.86
      );
    };
    img.onerror = () => done(file);
    img.src = url;
  });
}

/**
 * Gate + normalize one chosen file. Throws with a user-facing message.
 * @param {File} file
 * @returns {Promise<File>}
 */
export async function prepareUploadFile(file) {
  if (isHeicFile(file)) {
    throw new Error(
      'HEIC images are not supported yet. Export as JPG, PNG, or WebP and upload again.'
    );
  }
  if (!isAcceptedUploadFile(file)) {
    throw new Error('Unsupported image type. Use JPG, PNG, WebP, GIF, AVIF, or SVG.');
  }
  const next = await downscaleImageFile(file);
  if (next.size > MAX_UPLOAD_BYTES) {
    throw new Error('Image is too large. Use an image under 20 MB.');
  }
  return next;
}

/**
 * Make `host` a drop target for one image file.
 *
 * MK-4a widened this from the upload PANE to the whole dialog: the upload tab
 * was the only place a drop landed, so dropping a file onto the grid — the
 * obvious gesture once the grid is what fills the dialog — did nothing.
 *
 * `dragenter`/`dragleave` are counted rather than toggled. Both fire for every
 * descendant the pointer crosses, so a boolean flag clears the highlight the
 * moment the cursor moves from the grid onto a card inside it.
 *
 * @returns {() => void} teardown
 */
export function installDropTarget(host, onFile, { activeClass = 'pb-drag-over' } = {}) {
  if (!host) return () => {};
  let depth = 0;
  const paint = () => host.classList.toggle(activeClass, depth > 0);
  const hasFiles = (e) => [...(e.dataTransfer?.types || [])].includes('Files');

  const onDragEnter = (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    depth += 1;
    paint();
  };
  const onDragOver = (e) => {
    if (!hasFiles(e)) return;
    // Without this the browser navigates to the dropped file.
    e.preventDefault();
  };
  const onDragLeave = () => {
    depth = Math.max(0, depth - 1);
    paint();
  };
  const onDrop = (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    depth = 0;
    paint();
    const file = e.dataTransfer?.files?.[0];
    if (file) onFile(file);
  };

  host.addEventListener('dragenter', onDragEnter);
  host.addEventListener('dragover', onDragOver);
  host.addEventListener('dragleave', onDragLeave);
  host.addEventListener('drop', onDrop);
  return () => {
    host.removeEventListener('dragenter', onDragEnter);
    host.removeEventListener('dragover', onDragOver);
    host.removeEventListener('dragleave', onDragLeave);
    host.removeEventListener('drop', onDrop);
    host.classList.remove(activeClass);
  };
}

/**
 * A hidden <input type="file"> wired to `onFile`, plus its teardown.
 *
 * `accept` defaults to the picker's raster+SVG set. The SVG library passes the
 * SVG-only list: an `accept` narrower than the caller's own validation is a
 * convenience for the file dialog, never the gate — every consumer still
 * validates the chosen file, because a user can always pick "All Files".
 */
export function createFileInput(onFile, { accept = ACCEPTED_IMAGE_TYPES } = {}) {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = accept;
  input.className = 'pb-image-upload-input';
  input.hidden = true;
  const onChange = () => {
    const file = input.files?.[0];
    if (file) onFile(file);
    input.value = '';
  };
  input.addEventListener('change', onChange);
  return {
    el: input,
    open: () => input.click(),
    destroy: () => {
      input.removeEventListener('change', onChange);
      input.remove();
    }
  };
}
